/* eslint-disable no-console */
import { Helmet } from "react-helmet-async";
import { Route, Routes, Navigate } from "react-router-dom";
import { Loader, PrivateRoute, FichaEdificioInventario, VideoIntro } from "./components";
import { ADMIN_IDS, INVERSORES_RESTRINGIDOS, REGION_IDS } from "./constants/permisos";
import { ROLES } from "./constants/roles";
import { useAuthContext } from "./context/auth";
import AuthCallback from "./context/AuthCallback";
import {
  Admin,
  Api,
  CalculadorComisionEdificio,
  CargarCliente,
  CargarEdificio,
  CargarFactura,
  CargarIntermediario,
  CargarOportunidad,
  CarteleriaPage,
  Categoria,
  CategoriaAutomotriz,
  CategoriaBebidas,
  CategoriaDelivery,
  CategoriaFastFood,
  CategoriaSmartphones,
  CategoriaInversiones,
  CategoriaMascotas,
  CategoriaPerfumes,
  CategoriaRetail,
  CategoriaTelcos,
  CategoriaTransporte,
  CategoriaVacaciones,
  CategoriaVestimenta,
  ClientesCRM,
  ClientesCartera,
  ConciliacionSIIPage,
  CuentasBancariasPage,
  DashboardFinancieroPage,
  DashboardIvaPage,
  EstadoCuentaPage,
  FactoringPage,
  CotizadorProgrammatic,
  CotizadorProgrammaticEditar,
  ER,
  Edificios,
  FacturasEjecutivo,
  FlujoCajaV2Page,
  MovimientosBancariosPage,
  MisCotizacionesPage,
  GaleriaPage,
  ReporteFotograficoPage,
  ReporteFotograficoExternoPage,
  GestionActivosPage,
  GestionEmpleados,
  GestionProveedoresPage,
  MailingPage,
  Home,
  InventarioNuevo,
  InvestigacionIpsos,
  SimuladorImpacto,
  Landing,
  LandingTD,
  ListadoB2Bnuevo,
  ListadoCotizaciones,
  ListadoVetos,
  Login,
  Mapa,
  MapaCalor,
  Mediakit,
  MediakitEdificios,
  MediakitEdificiosFullCbre,
  NotFound,
  Oportunidad,
  OportunidadFactura,
  Perfil,
  PortafolioFichaEdificios,
  PuntosDeVenta,
  Redirect,
  TransformacionDigital,
  TransformacionDigitalVentas,
  Usuarios,
  VentasBTLpage,
  VentasDetalle,
  VentasEjecutivo,
  VentasEjecutivoV2,
  VentasFactura,
  VentasProyeccion,
  VentasRegionReciente,
} from "./pages";
import CotizacionesGeneradas from "./pages/CotizacionesGeneradas";
import MediakitEdificiosFull from "./pages/MediakitEdificiosFull";

const App = () => {
  const { session, pais, isLoading } = useAuthContext();

  const getMetaData = () => {
    if (pais === "Perú") {
      return {
        title: "Conectamos Marcas con Audiencias Premium",
        description: "Conectamos marcas y audiencias de manera eficiente.",
        canonical: "https://visiona.pe",
        hreflang: [
          { hreflang: "es-PE", href: "https://visiona.pe" },
          { hreflang: "es-CL", href: "https://wecast.cl" },
        ],
      };
    } else if (pais === "Chile") {
      return {
        title: "Conectamos Marcas con Audiencias Premium",
        description: "Conectamos marcas y audiencias de manera eficiente.",
        canonical: "https://wecast.cl",
        hreflang: [
          { hreflang: "es-PE", href: "https://visiona.pe" },
          { hreflang: "es-CL", href: "https://wecast.cl" },
        ],
      };
    } else {
      // Por defecto (si el país no está definido)
      return {
        title: "Conectamos Marcas con Audiencias Premium",
        description: "Conectamos marcas y audiencias de manera eficiente.",
        canonical: "https://visiona.pe",
        hreflang: [
          { hreflang: "es-PE", href: "https://visiona.pe" },
          { hreflang: "es-CL", href: "https://wecast.cl" },
        ],
      };
    }
  };

  const getIntroComponent = () => {
    if (pais === "Chile") {
      return <VideoIntro />;
    }
    return <NotFound />; // Or <Navigate to="/" replace /> if preferred
  };

  const { title, description, canonical, hreflang } = getMetaData();

  if (isLoading || session === undefined) return <Loader fullPage />;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="icon" type="image/svg+xml" href={pais === "Chile" ? "/favicon-w.svg" : "/favicon-v.svg"} />
        {hreflang.map((link) => (
          <link key={link.hreflang} rel="alternate" hreflang={link.hreflang} href={link.href} />
        ))}
      </Helmet>
      <Routes>
        <Route index element={<Home />} />
        <Route path="/auth/confirm" element={<AuthCallback />} />
        <Route path="/bitcoinapi" element={<Api />} />
        <Route path="/categorias/automotriz" element={<CategoriaAutomotriz />} />
        <Route path="/categorias/bebidas" element={<CategoriaBebidas />} />
        <Route path="/categorias/fastfood" element={<CategoriaFastFood />} />
        <Route path="/categorias/delivery" element={<CategoriaDelivery />} />
        <Route path="/categorias/smartphones" element={<CategoriaSmartphones />} />
        <Route path="/categorias/inversiones" element={<CategoriaInversiones />} />
        <Route path="/categorias/mascotas" element={<CategoriaMascotas />} />
        <Route path="/categorias/perfumes" element={<CategoriaPerfumes />} />
        <Route path="/categorias/retail" element={<CategoriaRetail />} />
        <Route path="/categorias/telcos" element={<CategoriaTelcos />} />
        <Route path="/categorias/transporte" element={<CategoriaTransporte />} />
        <Route path="/categorias/vacaciones" element={<CategoriaVacaciones />} />
        <Route path="/categorias/vestimenta" element={<CategoriaVestimenta />} />
        <Route path="/edificios" element={<MediakitEdificios />} />
        <Route path="/ficha-edificio/:id" element={<FichaEdificioInventario />} />
        <Route path="/galeria" element={<GaleriaPage />} />
        <Route path="/intro" element={getIntroComponent()} />
        <Route path="/inventario" element={<InventarioNuevo />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/listadoempresas" element={<ListadoB2Bnuevo />} />
        <Route path="/listadovetos" element={<ListadoVetos />} />
        <Route path="/mapa" element={<Mapa />} />
        <Route path="/mediakit" element={<Mediakit />} />
        <Route path="/mediakit-edificios-full" element={<MediakitEdificiosFull />} />
        <Route path="/mediakit-edificios-full-cbre" element={<MediakitEdificiosFullCbre />} />
        <Route path="/investigacion-ipsos" element={<InvestigacionIpsos />} />
        <Route path="/simulador-impacto" element={<SimuladorImpacto />} />
        <Route path="/portafolio" element={<PortafolioFichaEdificios />} />
        <Route path="/puntosdeventa" element={<PuntosDeVenta />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/td" element={<LandingTD />} />
        <Route path="/transformacion-digital" element={<TransformacionDigital />} />
        <Route path="/transformacion-digital-ventas" element={<TransformacionDigitalVentas />} />
        <Route path="/carteleriiaa" element={<CarteleriaPage />} />

        {session !== null ? (
          <>
            <Route
              path="/admin"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.ADMINISTRATIVO,
                    ROLES.AGENCIA,
                    ROLES.BOARD,
                    ROLES.COMERCIAL,
                    ROLES.CONTABLE,
                    ROLES.EDIFICIO,
                    ROLES.GERENCIA,
                    ROLES.INFRAESTRUCTURA,
                    ROLES.INVERSOR,
                    ROLES.SUPERADMIN,
                    ROLES.CLIENTE,
                  ]}
                >
                  <Admin />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/er"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.BOARD,
                    ROLES.CONTABLE,
                    ROLES.ADMINISTRATIVO,
                    ROLES.INVERSOR,
                  ]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <ER />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cargar-cliente"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD, ROLES.INVERSOR]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <CargarCliente />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cargar-factura"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}>
                  <CargarFactura />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cargar-intermediario"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA]}>
                  <CargarIntermediario />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cargar-oportunidad"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}>
                  <CargarOportunidad />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cargar-edificio"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.ADMINISTRATIVO]}>
                  <CargarEdificio />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/factura/:oportunidadId"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO]}>
                  <OportunidadFactura />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/oportunidades/editar/:oportunidadId"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO]}>
                  <Oportunidad />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/informes-comision"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.ADMINISTRATIVO,
                    ROLES.COMERCIAL,
                    ROLES.CONTABLE,
                    ROLES.EDIFICIO,
                    ROLES.BOARD,
                    ROLES.INVERSOR,
                  ]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <CalculadorComisionEdificio />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/online-cotizador"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.AGENCIA,
                    ROLES.CLIENTE,
                    ROLES.BOARD,
                    ROLES.INVERSOR,
                  ]}
                >
                  <CotizadorProgrammatic />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/mis-cotizaciones"
              element={
                <PrivateRoute allowedRoles={[ROLES.AGENCIA, ROLES.CLIENTE]}>
                  <MisCotizacionesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cotizaciones-generadas"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO]}>
                  <CotizacionesGeneradas />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/reporte-fotografico"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.ADMINISTRATIVO,
                    ROLES.BOARD,
                    ROLES.INVERSOR,
                  ]}
                >
                  <ReporteFotograficoPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/reporte-fotografico-externo"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.AGENCIA, ROLES.CLIENTE, ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}
                >
                  <ReporteFotograficoExternoPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/carteleriiaa"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO, ROLES.BOARD]}
                >
                  <CarteleriaPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/online-cotizador/editar/:id"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}>
                  <CotizadorProgrammaticEditar />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/ventas-BTL"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}>
                  <VentasBTLpage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/perfil"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.COMERCIAL, ROLES.GERENCIA]}>
                  <Perfil />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/usuarios"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.ADMINISTRATIVO,
                    ROLES.BOARD,
                    ROLES.INVERSOR,
                    ROLES.INFRAESTRUCTURA,
                  ]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <Usuarios />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/ventas-detalle"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.ADMINISTRATIVO,
                    ROLES.BOARD,
                    ROLES.INVERSOR,
                  ]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <VentasDetalle />
                </PrivateRoute>
              }
            />
            <Route path="/admin/ventas-facturas" element={<Navigate to="/admin/facturacion" replace />} />
            <Route
              path="/admin/facturacion"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.ADMINISTRATIVO,
                    ROLES.BOARD,
                    ROLES.CONTABLE,
                    ROLES.INVERSOR,
                  ]}
                >
                  <VentasFactura />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/ventas-proyeccion"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.ADMINISTRATIVO,
                    ROLES.BOARD,
                    ROLES.INVERSOR,
                    ROLES.CONTABLE,
                  ]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <VentasProyeccion />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/mis-ventas"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD, ROLES.INVERSOR]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <VentasEjecutivoV2 />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/mis-ventas-baca"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD]}>
                  <VentasEjecutivo />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/mapa-calor"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD, ROLES.INVERSOR]}
                >
                  <MapaCalor />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/gestion-activos"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                >
                  <GestionActivosPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/flujo-caja"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                >
                  <FlujoCajaV2Page />
                </PrivateRoute>
              }
            />
            <Route path="/admin/flujo-caja-old" element={<Navigate to="/admin/flujo-caja" replace />} />
            <Route
              path="/admin/dashboard-financiero"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                >
                  <DashboardFinancieroPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/dashboard-iva"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                >
                  <DashboardIvaPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/estado-cuenta"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                >
                  <EstadoCuentaPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/factoring"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                >
                  <FactoringPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/conciliacion-sii"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}>
                  <ConciliacionSIIPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/movimientos-bancarios"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}>
                  <MovimientosBancariosPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cuentas-bancarias"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO]}>
                  <CuentasBancariasPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/mis-facturas"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.ADMINISTRATIVO,
                    ROLES.BOARD,
                    ROLES.INVERSOR,
                    ROLES.CONTABLE,
                  ]}
                  deniedIds={INVERSORES_RESTRINGIDOS}
                >
                  <FacturasEjecutivo />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/ventas-region"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD]}
                  allowedIds={REGION_IDS}
                >
                  <VentasRegionReciente />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/edificios"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.ADMINISTRATIVO,
                    ROLES.BOARD,
                    ROLES.INFRAESTRUCTURA,
                    ROLES.COMERCIAL,
                    ROLES.CONTABLE,
                    ROLES.INVERSOR,
                  ]}
                >
                  <Edificios />
                </PrivateRoute>
              }
            />
            <Route
              path="/categorias"
              element={
                <PrivateRoute
                  allowedRoles={[
                    ROLES.SUPERADMIN,
                    ROLES.GERENCIA,
                    ROLES.COMERCIAL,
                    ROLES.AGENCIA,
                    ROLES.CLIENTE,
                    ROLES.BOARD,
                    ROLES.EDIFICIO,
                  ]}
                >
                  <Categoria />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/cotizaciones"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD]}>
                  <ListadoCotizaciones />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/clientes"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD]}>
                  <ClientesCRM />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/clientes-cartera"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO, ROLES.BOARD]}
                >
                  <ClientesCartera />
                </PrivateRoute>
              }
            />
            <Route path="/login" element={<Navigate to="/admin" replace />} />
            <Route
              path="/admin/mailing"
              element={
                <PrivateRoute
                  allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO]}
                  allowedIds={ADMIN_IDS}
                >
                  <MailingPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/gestion-empleados"
              element={
                <PrivateRoute allowedRoles={[ROLES.GERENCIA, ROLES.ADMINISTRATIVO]}>
                  <GestionEmpleados />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/proveedores"
              element={
                <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}>
                  <GestionProveedoresPage />
                </PrivateRoute>
              }
            />
          </>
        ) : (
          <>
            <Route path="*" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
          </>
        )}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

export default App;
